import React from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Gem, RefreshCw, Palette, FileText, FolderTree, Newspaper, HelpCircle, Tag, Shuffle, Store, Star, Inbox, Users, Crown, Images, Check, ShieldCheck, Heart, LayoutGrid, ArrowLeftRight, CalendarClock, Gift, Instagram, MailPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS: [string, string, any][] = [
  ["/admin", "Dashboard", LayoutDashboard], ["/admin/orders", "Orders", ShoppingCart], ["/admin/products", "Products", Gem],
  ["/admin/sync", "Catalog Sync", RefreshCw], ["/admin/site", "Site, Menus & Home", Palette], ["/admin/content/pages", "Pages & Education", FileText],
  ["/admin/content/collections", "Collections", FolderTree], ["/admin/style-tiles", "Style Tiles", LayoutGrid], ["/admin/page-nav", "Prev / Next Buttons", ArrowLeftRight], ["/admin/content/posts", "Blog", Newspaper], ["/admin/content/faqs", "FAQ", HelpCircle],
  ["/admin/content/promos", "Promo Codes", Tag], ["/admin/instalments", "Pay in Instalments", CalendarClock], ["/admin/gift-guide", "Gift Guide", Gift], ["/admin/giveaway", "Instagram Giveaway", Instagram], ["/admin/signup-popup", "Sign-up Popup", MailPlus], ["/admin/seo", "SEO & Redirects", Shuffle], ["/admin/replaced-photos", "Replaced Photos", Images], ["/admin/content/stores", "Store Locator", Store],
  ["/admin/provenance", "Provenance & NFC", ShieldCheck],
  ["/admin/loan-rings", "Loan Rings", Gem],
  ["/admin/stories", "Said Yes Stories", Heart], ["/admin/reviews", "Reviews", Star], ["/admin/rewards", "Somnio VIP", Crown], ["/admin/inbox", "Inbox", Inbox], ["/admin/users", "Users", Users],
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen grid md:grid-cols-[240px_1fr] bg-[#F7F6F2]" data-testid="admin-layout">
      <aside className="bg-[#141414] text-white/80 md:min-h-screen p-5 md:sticky md:top-0 md:h-screen overflow-y-auto">
        <Link to="/" className="block font-wordmark text-lg text-white mb-1" data-testid="admin-wordmark">SOMNIO.CO</Link>
        <p className="eyebrow text-[9px] text-white/40 mb-8">CMS & Commerce</p>
        <nav className="flex md:flex-col gap-1 overflow-x-auto">
          {LINKS.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-white/10 text-white" : "hover:bg-white/5 hover:text-white"}`}>
              <Icon size={16} strokeWidth={1.4} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-10 text-xs text-white/40 hidden md:block">{user?.email}<button onClick={logout} className="block mt-2 underline hover:text-white" data-testid="admin-logout">Sign out</button></div>
      </aside>
      <main className="p-5 md:p-10 min-w-0"><Outlet /></main>
    </div>
  );
}

export const AdminTitle = ({ title, sub, children }: any) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div><h1 className="font-display text-4xl font-light" data-testid="admin-page-title">{title}</h1>{sub && <p className="text-sm text-neutral-500 mt-1">{sub}</p>}</div>
    <div className="flex gap-3">{children}</div>
  </div>
);

export const Btn = ({ children, ghost = false, ...p }: any) => (
  <button {...p} className={`${ghost ? "border border-neutral-300 bg-white hover:border-neutral-900" : "bg-neutral-900 text-white hover:bg-[var(--c-primary)]"} px-4 py-2 text-xs tracking-wider uppercase transition-colors disabled:opacity-50 ${p.className || ""}`}>{children}</button>
);

const stampFmt = (d: string) => new Date(d).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const dirtyRegistry = new Set<string>();
export const hasUnsaved = () => dirtyRegistry.size > 0;
export const confirmLeave = () => !hasUnsaved() || window.confirm("You have unsaved changes. Leave without saving?");

// Warn before closing the tab or following a link while any SaveBtn is dirty.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => { if (hasUnsaved()) { e.preventDefault(); e.returnValue = ""; } });
  document.addEventListener("click", (e) => {
    if (!hasUnsaved()) return;
    const a = (e.target as HTMLElement)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target === "_blank" || e.defaultPrevented || a.getAttribute("href")?.startsWith("#")) return;
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("You have unsaved changes. Leave without saving?")) return;
    dirtyRegistry.clear();
    // Drop the parked Back-guard entry first, then follow the link.
    if (window.history.state?.unsavedGuard) { window.history.back(); setTimeout(() => a.click(), 60); } else a.click();
  }, true);
}

function useUnsavedGuard(id: string, dirty: boolean) {
  React.useEffect(() => {
    if (!dirty) return;
    const first = !hasUnsaved();
    dirtyRegistry.add(id);
    // Back/forward guard: park a duplicate history entry so the first Back press stays on this page.
    if (first) window.history.pushState({ unsavedGuard: true }, "", window.location.href);
    return () => {
      dirtyRegistry.delete(id);
      // Closed without saving (e.g. editor dialog): remove the parked entry if we're still on it.
      if (!hasUnsaved() && window.history.state?.unsavedGuard) setTimeout(() => { if (!hasUnsaved() && window.history.state?.unsavedGuard) window.history.back(); }, 0);
    };
  }, [id, dirty]);
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    if (!hasUnsaved()) return;
    if (window.confirm("You have unsaved changes. Leave without saving?")) { dirtyRegistry.clear(); window.history.back(); }
    else window.history.pushState({ unsavedGuard: true }, "", window.location.href);
  });
}

// Save button that turns into "Saved" until the value changes again, with a last-saved stamp underneath.
const STAMP_KEYS = new Set(["saved_at", "saved_by", "updated_at", "updated_by", "intro_active", "hero_active"]);

export function SaveBtn({ onSave, value, stampKey, savedAt, savedBy, label = "Save Changes", testId, className = "", onDirty }: any) {
  const { user } = useAuth();
  const snap = (v: any) => JSON.stringify(v ?? null, (k, x) => (STAMP_KEYS.has(k) ? undefined : x && typeof x === "object" && !Array.isArray(x) ? Object.fromEntries(Object.keys(x).sort().map((n) => [n, x[n]])) : x));
  const rebaseUntil = React.useRef(0);
  const savedVal = React.useRef<any>(null);
  // After a save, accept the server's echo (extra or reordered fields) as saved, but never a new user edit.
  const isEcho = (v: any) => { const s = savedVal.current; if (!s || typeof s !== "object" || Array.isArray(s) || !v || typeof v !== "object") return snap(v) === snap(s); return snap(Object.fromEntries(Object.keys(s).map((k) => [k, v[k]]))) === snap(s); };
  const stored = (stampKey && localStorage.getItem(`saved:${stampKey}`)) || "";
  const [base, setBase] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [flash, setFlash] = React.useState(false);
  const [stamp, setStamp] = React.useState<string>(() => savedAt || stored.split("|")[0]);
  const [by, setBy] = React.useState<string>(() => savedBy || stored.split("|")[1] || "");
  React.useEffect(() => { if (value != null && (base === null || (Date.now() < rebaseUntil.current && isEcho(value)))) setBase(snap(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (savedAt) setStamp(savedAt); if (savedBy) setBy(savedBy); }, [savedAt, savedBy]);
  const dirty = value !== undefined && base !== null && base !== snap(value);
  const saved = value !== undefined ? !dirty && base !== null && !!stamp : flash;
  useUnsavedGuard(testId, dirty);
  React.useEffect(() => { onDirty?.(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const click = async () => {
    setBusy(true);
    try {
      if ((await onSave()) === false) return;
      const now = new Date().toISOString();
      const name = user?.name || user?.email || "";
      setStamp(now); setBy(name); if (stampKey) localStorage.setItem(`saved:${stampKey}`, `${now}|${name}`);
      setBase(snap(value)); savedVal.current = JSON.parse(JSON.stringify(value ?? null)); rebaseUntil.current = Date.now() + 2500; setFlash(true); setTimeout(() => setFlash(false), 4000);
    } finally { setBusy(false); }
  };
  return (
    <div className={`inline-flex flex-col items-end gap-1 ${className}`}>
      <Btn onClick={click} disabled={busy} data-testid={testId} className={saved ? "!bg-emerald-700" : dirty ? "!bg-[var(--c-primary)]" : ""}>
        {busy ? "Saving…" : saved ? <span className="inline-flex items-center gap-1.5"><Check size={13} />Saved</span> : label}
      </Btn>
      {stamp && <span className="text-[10px] text-neutral-500 tracking-wide" data-testid={`${testId}-stamp`}>Last saved {stampFmt(stamp)}{by && <> by <span className="text-neutral-800" data-testid={`${testId}-by`}>{by}</span></>}</span>}
      {dirty && <span className="text-[10px] text-amber-700" data-testid={`${testId}-unsaved`}>Unsaved changes</span>}
    </div>
  );
}

export const Pill = ({ tone = "neutral", children }: any) => {
  const c: any = { green: "bg-emerald-50 text-emerald-800", red: "bg-red-50 text-red-800", amber: "bg-amber-50 text-amber-800", neutral: "bg-neutral-100 text-neutral-700" };
  return <span className={`inline-block px-2 py-0.5 text-[11px] ${c[tone]}`}>{children}</span>;
};

export const Table = ({ head, children, testId }: any) => (
  <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 text-left"><tr>{head.map((h: string) => <th key={h} className="px-4 py-3 eyebrow text-[10px] text-neutral-500 font-normal">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-neutral-100">{children}</tbody>
    </table>
  </div>
);
